import { Check, Package, PanelsTopLeft, ClipboardCheck } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { DarkButton, ArrowLink } from "./Buttons";
import { SERVICE_LINKS } from "./config";

const cardMotion =
  "transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 focus-within:-translate-y-1";
const iconMotion =
  "transition-transform duration-200 ease-out group-hover/card:-translate-y-0.5 group-hover/card:-rotate-2 group-focus-within/card:-translate-y-0.5 group-focus-within/card:-rotate-2";

const PACKAGE_ITEMS = [
  "Wholesale peptide supply",
  "White-label vials & packaging",
  "Brand identity & custom storefront",
  "3PL fulfillment setup",
];

const SecondaryCard = ({ icon: Icon, title, body, meta, cta, target, testId, ctaTestId, delay }) => (
  <Reveal delay={delay}>
    <article
      data-testid={testId}
      className={`group/card h-full rounded-3xl border border-white/[0.08] bg-card p-7 sm:p-8 ${cardMotion} hover:border-lilac/35 hover:shadow-[0_24px_50px_-24px_rgba(0,0,0,0.7)] focus-within:border-lilac/35 focus-within:shadow-[0_24px_50px_-24px_rgba(0,0,0,0.7)]`}
    >
      <div className="flex items-start gap-5">
        <span
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-lilac-bright to-lilac-deep text-ink shadow-[0_10px_20px_-8px_rgba(154,131,216,0.6)] ${iconMotion}`}
        >
          <Icon className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-display text-xl font-extrabold text-paper">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-paper/60">{body}</p>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.14em] text-paper/35">{meta}</p>
          <ArrowLink
            testId={ctaTestId}
            target={target}
            className="mt-4 group-hover/card:text-lilac-bright group-hover/card:after:scale-x-100 group-focus-within/card:text-lilac-bright group-focus-within/card:after:scale-x-100"
          >
            {cta}
          </ArrowLink>
        </div>
      </div>
    </article>
  </Reveal>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="scroll-mt-24 py-20 sm:py-28" tabIndex={-1}>
    <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
      <Reveal>
        <Eyebrow index="01" testId="services-eyebrow">
          The offering
        </Eyebrow>
        <h2 className="mt-4 max-w-xl font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
          Supply to doorstep. All of it.
        </h2>
        <p className="mt-4 max-w-md text-base text-paper/60">
          Wholesale peptides, white-label vials, your brand and website, and fulfillment — choose the complete
          setup or the piece you need next.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-6 lg:grid-cols-[1.08fr_1fr]">
        <Reveal className="h-full">
          <article
            data-testid="service-card-package"
            className={`group/card relative h-full overflow-hidden rounded-3xl bg-gradient-to-br from-[#c8b8f0] via-lilac to-[#a78fe0] p-8 text-ink sm:p-10 ${cardMotion} hover:shadow-[0_30px_60px_-24px_rgba(154,131,216,0.5)] focus-within:shadow-[0_30px_60px_-24px_rgba(154,131,216,0.5)]`}
          >
            <span
              className={`absolute right-8 top-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-ink/90 text-lilac shadow-[0_14px_28px_-10px_rgba(0,0,0,0.5)] sm:right-10 sm:top-10 ${iconMotion}`}
            >
              <Package className="h-6 w-6" strokeWidth={2.2} aria-hidden="true" />
            </span>

            <p className="text-xs font-bold uppercase tracking-[0.22em] text-ink/55">The complete start</p>
            <h3 className="mt-4 max-w-[70%] font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              Business in a box
            </h3>
            <p className="mt-4 max-w-sm text-base text-ink/70">
              Supply, vials, brand, website, and fulfillment — brought together.
            </p>

            <ul className="mt-8 space-y-3.5">
              {PACKAGE_ITEMS.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm font-bold text-ink/85">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink/90 text-lilac">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <DarkButton
              testId="service-cta-package"
              target={SERVICE_LINKS.package}
              className="mt-9 group-hover/card:bg-[#2b2c3e] group-hover/card:shadow-[0_18px_36px_-12px_rgba(0,0,0,0.75)]"
            >
              Explore the package
            </DarkButton>
          </article>
        </Reveal>

        <div className="grid gap-6">
          <SecondaryCard
            icon={PanelsTopLeft}
            title="Website creation"
            body="A custom online store, built around your brand."
            meta="Design · Catalog · Checkout"
            cta="Explore websites"
            target={SERVICE_LINKS.websites}
            testId="service-card-website"
            ctaTestId="service-cta-website"
            delay={0.08}
          />
          <SecondaryCard
            icon={ClipboardCheck}
            title="Launch checklist"
            body="A clear path through the details before you go live."
            meta="Plan · Prepare · Review"
            cta="See the checklist"
            target={SERVICE_LINKS.checklist}
            testId="service-card-checklist"
            ctaTestId="service-cta-checklist"
            delay={0.16}
          />
        </div>
      </div>
    </div>
  </section>
);
